import {
  Box,
  Flex,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import * as api from 'src/brain/api';

const idOf = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') return String(value._id ?? value);
  return String(value);
};

/** Map place entity_type → leaf childModel for acceptedChildren payloads. */
function childModelForEntityType(entityType) {
  if (entityType === 'permit' || entityType === 'timedentry_tour') {
    return 'Permits';
  }
  if (entityType === 'hotel') return 'Rooms';
  return 'Campsites';
}

/**
 * Inline leaf-unit picker for one trip place (e.g. camping vs refugio at a
 * Torres location). Fetches via GET /campsites?outingId= which already returns
 * Campsites / Permits / Rooms / TourSlots by place type.
 *
 * Empty `selectedChildren` means "any accommodation" (backward-compatible
 * unfiltered) and is shown as all options selected in the UI.
 */
export default function NightBookablePicker({
  placeOutingId,
  placeName,
  entityType,
  selectedChildren = [],
  groupSize = 1,
  onChange,
  groupBy = (c) => c.type || 'Other',
}) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const childModel = childModelForEntityType(entityType);
  const explicitSelection = (selectedChildren ?? []).length > 0;
  const selectedIds = useMemo(() => {
    if (!explicitSelection) {
      return new Set(children.map((c) => idOf(c._id)));
    }
    return new Set((selectedChildren ?? []).map((c) => idOf(c.childId)));
  }, [selectedChildren, children, explicitSelection]);

  useEffect(() => {
    let cancelled = false;
    if (!placeOutingId) return undefined;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const { success, failure } = await api.getCampsites({
          outingId: placeOutingId,
        });
        if (cancelled) return;
        if (failure) {
          setError('Could not load options');
          setChildren([]);
          return;
        }
        setChildren(success?.campsites ?? []);
      } catch (err) {
        if (!cancelled) {
          setError('Could not load options');
          setChildren([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [placeOutingId]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const c of children) {
      const key = String(groupBy(c) || 'Other');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return Array.from(map.entries());
  }, [children, groupBy]);

  const toLeaf = (campsite) => ({
    childModel,
    childId: idOf(campsite._id),
    name: campsite.name,
  });

  const toggle = (campsite) => {
    const id = idOf(campsite._id);
    // Leaving "any" (empty): first click narrows to all-except-this.
    if (!explicitSelection) {
      const next = children.filter((c) => idOf(c._id) !== id).map(toLeaf);
      onChange?.(next);
      return;
    }
    if (selectedIds.has(id)) {
      const next = (selectedChildren ?? []).filter(
        (c) => idOf(c.childId) !== id
      );
      // Cleared back to none → treat as "any" again.
      onChange?.(next.length ? next : []);
      return;
    }
    const next = [...(selectedChildren ?? []), toLeaf(campsite)];
    // Selecting every option returns to unfiltered "any".
    if (next.length >= children.length) {
      onChange?.([]);
      return;
    }
    onChange?.(next);
  };

  if (!placeOutingId) return null;

  return (
    <Flex direction="column" gap={1.5} w="100%" pl={1}>
      <Flex justify="space-between" align="baseline">
        <Text fontSize="xs" fontWeight={600} color="gray.600">
          At {placeName}
        </Text>
        <Text fontSize="2xs" color="gray.500">
          {explicitSelection
            ? `${selectedIds.size} selected`
            : 'Any accommodation'}
        </Text>
      </Flex>

      {loading && (
        <Flex align="center" gap={2} py={1}>
          <Spinner size="xs" color="brand.500" />
          <Text fontSize="xs" color="gray.500">
            Loading…
          </Text>
        </Flex>
      )}

      {error && (
        <Text fontSize="xs" color="red.500">
          {error}
        </Text>
      )}

      {!loading && !error && children.length === 0 && (
        <Text fontSize="xs" color="gray.500">
          No specific options for this place
        </Text>
      )}

      {!loading &&
        groups.map(([label, items]) => (
          <Box key={label}>
            {groups.length > 1 && (
              <Text
                fontSize="2xs"
                color="gray.500"
                textTransform="capitalize"
                mb={1}
              >
                {label}
              </Text>
            )}
            <Wrap spacing={1.5}>
              {items.map((c) => {
                const id = idOf(c._id);
                const selected = selectedIds.has(id);
                const tooSmall =
                  Number(c.maxSize) > 0 && Number(c.maxSize) < groupSize;
                return (
                  <WrapItem key={id}>
                    <Tag
                      size="sm"
                      variant={selected ? 'solid' : 'outline'}
                      colorScheme={tooSmall ? 'orange' : 'brand'}
                      cursor="pointer"
                      opacity={c.reservable === false ? 0.5 : 1}
                      onClick={() => toggle(c)}
                      title={
                        tooSmall
                          ? `Max ${c.maxSize} — smaller than your group of ${groupSize}`
                          : undefined
                      }
                    >
                      <TagLabel>{c.name}</TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          </Box>
        ))}
    </Flex>
  );
}

export { childModelForEntityType, idOf as leafIdOf };
